import { useState, useMemo, useEffect } from 'react';
import { useAdminStore } from '../store/adminStore';
import { Order, OrderStatus, ORDER_STATUS_LABELS, ORDER_STATUS_COLORS } from '../types/admin';
import { downloadInvoice } from '../utils/generateInvoice';
import BarcodeGenerator from '../components/BarcodeGenerator';
import BarcodePrintTemplates from '../components/BarcodePrintTemplates';
import {
  Search,
  Eye,
  Trash2,
  ChevronDown,
  X,
  Clock,
  Package,
  Truck,
  CheckCircle,
  XCircle,
  MapPin,
  CreditCard,
  StickyNote,
  RefreshCw,
  FileText,
  QrCode,
  Barcode,
  Printer,
  AlertTriangle,
} from 'lucide-react';

export default function AdminOrders() {
  const { orders, updateOrderStatus, deleteOrder, fetchOrdersFromAPI } = useAdminStore();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<OrderStatus | 'all'>('all');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [barcodeOrder, setBarcodeOrder] = useState<Order | null>(null);
  const [barcodeType, setBarcodeType] = useState<'qr' | 'barcode'>('qr');
  const [shippingLabelOrder, setShippingLabelOrder] = useState<Order | null>(null);

  // Sayfa açılır açılmaz güncel veriyi çek; arka plan senkronizasyonu artık
  // AdminLayout tarafından panel genelinde yönetiliyor.
  useEffect(() => {
    fetchOrdersFromAPI();
  }, []);

  const filteredOrders = useMemo(() => {
    return orders
      .filter((o) => {
        const matchesSearch =
          o.orderNumber.toLowerCase().includes(search.toLowerCase()) ||
          o.customerName.toLowerCase().includes(search.toLowerCase()) ||
          o.customerEmail.toLowerCase().includes(search.toLowerCase());
        const matchesStatus = statusFilter === 'all' || o.status === statusFilter;
        return matchesSearch && matchesStatus;
      })
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [orders, search, statusFilter]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: orders.length };
    orders.forEach((o) => {
      counts[o.status] = (counts[o.status] || 0) + 1;
    });
    return counts;
  }, [orders]);

  const getStatusIcon = (status: OrderStatus) => {
    const icons: Record<OrderStatus, React.ReactNode> = {
      pending: <Clock className="w-4 h-4" />,
      processing: <Package className="w-4 h-4" />,
      shipped: <Truck className="w-4 h-4" />,
      in_transit: <RefreshCw className="w-4 h-4" />,
      delivered: <CheckCircle className="w-4 h-4" />,
      cancelled: <XCircle className="w-4 h-4" />,
      returned: <RefreshCw className="w-4 h-4" />,
      return_requested: <AlertTriangle className="w-4 h-4" />,
    };
    return icons[status];
  };

  const handleStatusChange = (orderId: string, newStatus: OrderStatus) => {
    updateOrderStatus(orderId, newStatus);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Sipariş Yönetimi</h1>
        <p className="text-gray-500 mt-1">{orders.length} sipariş mevcut</p>
      </div>

      {/* Status Tabs */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setStatusFilter('all')}
          className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
            statusFilter === 'all'
              ? 'bg-brand-red text-white'
              : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
          }`}
        >
          Tümü ({statusCounts.all || 0})
        </button>
        {(Object.keys(ORDER_STATUS_LABELS) as OrderStatus[]).map((status) => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              statusFilter === status
                ? 'bg-brand-red text-white'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {ORDER_STATUS_LABELS[status]} ({statusCounts[status] || 0})
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Sipariş no, müşteri ara..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
        />
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                <th className="px-6 py-4">Sipariş No</th>
                <th className="px-6 py-4">Müşteri</th>
                <th className="px-6 py-4">Ürünler</th>
                <th className="px-6 py-4">Tutar</th>
                <th className="px-6 py-4">Ödeme</th>
                <th className="px-6 py-4">Durum</th>
                <th className="px-6 py-4">Tarih</th>
                <th className="px-6 py-4">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredOrders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <span className="font-mono text-sm font-medium text-brand-red">
                      {order.orderNumber}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{order.customerName}</p>
                      <p className="text-xs text-gray-500">{order.customerPhone}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-600">
                      {order.items.length} ürün
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        ₺{order.totalAmount.toLocaleString('tr-TR')}
                      </p>
                      {order.shippingCost > 0 && (
                        <p className="text-xs text-gray-500">+₺{order.shippingCost} kargo</p>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                      order.paymentStatus === 'paid' ? 'bg-green-100 text-green-700' :
                      order.paymentStatus === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {order.paymentStatus === 'paid' ? 'Ödendi' :
                       order.paymentStatus === 'pending' ? 'Bekliyor' :
                       order.paymentStatus === 'failed' ? 'Başarısız' : 'İade'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="relative">
                      <select
                        value={order.status}
                        onChange={(e) => handleStatusChange(order.id, e.target.value as OrderStatus)}
                        className={`appearance-none pl-8 pr-8 py-1.5 rounded-lg text-xs font-medium border-0 focus:outline-none focus:ring-2 focus:ring-brand-red/20 cursor-pointer ${ORDER_STATUS_COLORS[order.status]}`}
                      >
                        {(Object.keys(ORDER_STATUS_LABELS) as OrderStatus[]).map((status) => (
                          <option key={status} value={status}>{ORDER_STATUS_LABELS[status]}</option>
                        ))}
                      </select>
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none">
                        {getStatusIcon(order.status)}
                      </span>
                      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 pointer-events-none" />
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-500">
                      {new Date(order.createdAt).toLocaleDateString('tr-TR')}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setSelectedOrder(order)}
                        className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-blue-600 transition-colors"
                        title="Detay"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setBarcodeOrder(order)}
                        className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-purple-600 transition-colors"
                        title="Barkod/QR"
                      >
                        <QrCode className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setShippingLabelOrder(order)}
                        className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-orange-600 transition-colors"
                        title="Kargo Etiketi"
                      >
                        <Printer className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          downloadInvoice(order, {
                            name: 'T-RaX Car Care',
                            address: 'Yeni Birlik Sanayi Sitesi, Saray, 675. Cadde No:24/12, 06980 Kahramankazan/Ankara',
                            phone: '0312 815 11 65',
                            email: 'info@sbtkimya.com.tr',
                            taxRate: 20,
                          });
                        }}
                        className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-brand-red transition-colors"
                        title="Fatura İndir"
                      >
                        <FileText className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(order.id)}
                        className="p-2 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600 transition-colors"
                        title="Sil"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredOrders.length === 0 && (
          <div className="text-center py-12">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Sipariş bulunamadı</p>
          </div>
        )}
      </div>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Sipariş Detayı</h2>
                <p className="text-sm text-gray-500 font-mono">{selectedOrder.orderNumber}</p>
              </div>
              <button
                onClick={() => setSelectedOrder(null)}
                className="p-2 hover:bg-gray-100 rounded-lg text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Status */}
              <div className="flex items-center gap-4">
                <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold ${ORDER_STATUS_COLORS[selectedOrder.status]}`}>
                  {getStatusIcon(selectedOrder.status)}
                  {ORDER_STATUS_LABELS[selectedOrder.status]}
                </span>
                <span className={`inline-flex px-3 py-1.5 rounded-lg text-xs font-medium ${
                  selectedOrder.paymentStatus === 'paid' ? 'bg-green-100 text-green-700' :
                  selectedOrder.paymentStatus === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {selectedOrder.paymentStatus === 'paid' ? 'Ödendi' :
                   selectedOrder.paymentStatus === 'pending' ? 'Bekliyor' :
                   selectedOrder.paymentStatus === 'failed' ? 'Başarısız' : 'İade'}
                </span>
              </div>

              {/* Customer Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                    <MapPin className="w-4 h-4" />
                    Müşteri Bilgileri
                  </h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-gray-500">Ad Soyad:</span> <span className="font-medium">{selectedOrder.customerName}</span></p>
                    <p><span className="text-gray-500">E-posta:</span> <span className="font-medium">{selectedOrder.customerEmail}</span></p>
                    <p><span className="text-gray-500">Telefon:</span> <span className="font-medium">{selectedOrder.customerPhone}</span></p>
                    <p><span className="text-gray-500">Adres:</span> <span className="font-medium">{selectedOrder.shippingAddress}</span></p>
                    <p><span className="text-gray-500">İlçe/İl:</span> <span className="font-medium">{selectedOrder.district}/{selectedOrder.city}</span></p>
                    {selectedOrder.postalCode && (
                      <p><span className="text-gray-500">Posta Kodu:</span> <span className="font-medium">{selectedOrder.postalCode}</span></p>
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                    <CreditCard className="w-4 h-4" />
                    Ödeme Bilgileri
                  </h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-gray-500">Yöntem:</span> <span className="font-medium">
                      {selectedOrder.paymentMethod === 'credit_card' ? 'Kredi Kartı' :
                       selectedOrder.paymentMethod === 'bank_transfer' ? 'Havale/EFT' :
                       'Kapıda Ödeme'}
                    </span></p>
                    <p><span className="text-gray-500">Tutar:</span> <span className="font-semibold text-brand-red">₺{selectedOrder.totalAmount.toLocaleString('tr-TR')}</span></p>
                    <p><span className="text-gray-500">Kargo:</span> <span className="font-medium">
                      {selectedOrder.shippingCost > 0 ? `₺${selectedOrder.shippingCost}` : 'Ücretsiz'}
                    </span></p>
                  </div>
                </div>
              </div>

              {/* Return Request Info */}
              {selectedOrder.returnRequest && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <h3 className="flex items-center gap-2 text-sm font-semibold text-amber-700 mb-2">
                    🔄 İade Talebi
                  </h3>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-amber-600">Sebep:</span> <span className="font-medium">{selectedOrder.returnRequest.reason}</span></p>
                    {selectedOrder.returnRequest.description && (
                      <p><span className="text-amber-600">Açıklama:</span> <span className="font-medium">{selectedOrder.returnRequest.description}</span></p>
                    )}
                    <p><span className="text-amber-600">Talep Tarihi:</span> <span className="font-medium">{new Date(selectedOrder.returnRequest.requestedAt).toLocaleString('tr-TR')}</span></p>
                  </div>
                </div>
              )}

              {/* Customer's Other Orders */}
              {(() => {
                const customerOrders = orders.filter(o => o.customerEmail === selectedOrder.customerEmail && o.id !== selectedOrder.id);
                if (customerOrders.length === 0) return null;
                return (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <h3 className="text-sm font-semibold text-blue-700 mb-2">
                      📋 Bu Müşterinin Diğer Siparişleri ({customerOrders.length})
                    </h3>
                    <div className="space-y-2">
                      {customerOrders.slice(0, 5).map(co => (
                        <div key={co.id} className="flex items-center justify-between bg-white rounded-lg p-2 text-xs">
                          <div>
                            <span className="font-mono font-medium">{co.orderNumber}</span>
                            <span className="text-gray-400 ml-2">{new Date(co.createdAt).toLocaleDateString('tr-TR')}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">₺{co.totalAmount.toLocaleString('tr-TR')}</span>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${ORDER_STATUS_COLORS[co.status]}`}>
                              {ORDER_STATUS_LABELS[co.status]}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Products */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Sipariş Edilen Ürünler</h3>
                <div className="space-y-3">
                  {selectedOrder.items.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl">
                      <div className="w-16 h-16 bg-white rounded-lg overflow-hidden flex-shrink-0">
                        {item.product.images?.[0] ? (
                          <img
                            src={item.product.images[0]}
                            alt={item.product.name}
                            className="w-full h-full object-contain p-1"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="w-6 h-6 text-gray-300" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{item.product.name}</p>
                        <p className="text-xs text-gray-500">Miktar: {item.quantity}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-gray-900">
                          ₺{(item.product.salePrice * item.quantity).toLocaleString('tr-TR')}
                        </p>
                        <p className="text-xs text-gray-500">₺{item.product.salePrice} / adet</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes */}
              {selectedOrder.notes && (
                <div className="bg-yellow-50 rounded-xl p-4">
                  <h3 className="flex items-center gap-2 text-sm font-semibold text-yellow-700 mb-2">
                    <StickyNote className="w-4 h-4" />
                    Notlar
                  </h3>
                  <p className="text-sm text-yellow-800">{selectedOrder.notes}</p>
                </div>
              )}

              {/* Timeline */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Zaman Çizelgesi</h3>
                <div className="space-y-2 text-sm text-gray-600">
                  <p>Oluşturulma: {new Date(selectedOrder.createdAt).toLocaleString('tr-TR')}</p>
                  <p>Son Güncelleme: {new Date(selectedOrder.updatedAt).toLocaleString('tr-TR')}</p>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShippingLabelOrder(selectedOrder);
                }}
                className="flex items-center gap-2 px-4 py-2.5 bg-orange-600 hover:bg-orange-700 text-white rounded-xl font-medium transition-colors"
              >
                <Printer className="w-4 h-4" />
                Kargo Etiketi
              </button>
              <button
                onClick={() => {
                  setBarcodeOrder(selectedOrder);
                }}
                className="flex items-center gap-2 px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-medium transition-colors"
              >
                <QrCode className="w-4 h-4" />
                Barkod/QR
              </button>
              <button
                onClick={() => {
                  downloadInvoice(selectedOrder, {
                    name: 'T-RaX Car Care',
                    address: 'Yeni Birlik Sanayi Sitesi, Saray, 675. Cadde No:24/12, 06980 Kahramankazan/Ankara',
                    phone: '0312 815 11 65',
                    email: 'info@sbtkimya.com.tr',
                    taxRate: 20,
                  });
                }}
                className="flex items-center gap-2 px-4 py-2.5 bg-brand-red hover:bg-red-700 text-white rounded-xl font-medium transition-colors"
              >
                <FileText className="w-4 h-4" />
                Fatura İndir
              </button>
              <button
                onClick={() => setSelectedOrder(null)}
                className="px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Shipping Label Modal */}
      {shippingLabelOrder && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Kargo Etiketi</h2>
                <p className="text-sm text-gray-500 font-mono">{shippingLabelOrder.orderNumber}</p>
              </div>
              <button
                onClick={() => setShippingLabelOrder(null)}
                className="p-2 hover:bg-gray-100 rounded-lg text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <BarcodePrintTemplates
                order={{
                  orderNumber: shippingLabelOrder.orderNumber,
                  customerName: shippingLabelOrder.customerName,
                  address: shippingLabelOrder.shippingAddress,
                  city: shippingLabelOrder.city,
                  district: shippingLabelOrder.district,
                  phone: shippingLabelOrder.customerPhone,
                }}
                mode="shipping"
                onClose={() => setShippingLabelOrder(null)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Barcode/QR Modal */}
      {barcodeOrder && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Barkod / QR Kod</h2>
                <p className="text-sm text-gray-500 font-mono">{barcodeOrder.orderNumber}</p>
              </div>
              <button
                onClick={() => setBarcodeOrder(null)}
                className="p-2 hover:bg-gray-100 rounded-lg text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Type Toggle */}
            <div className="flex gap-2 px-6 pt-4">
              <button
                onClick={() => setBarcodeType('qr')}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  barcodeType === 'qr'
                    ? 'bg-brand-red text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <QrCode className="w-4 h-4" />
                QR Kod
              </button>
              <button
                onClick={() => setBarcodeType('barcode')}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  barcodeType === 'barcode'
                    ? 'bg-brand-red text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Barcode className="w-4 h-4" />
                Barkod
              </button>
            </div>

            <div className="p-6">
              <BarcodeGenerator
                data={barcodeOrder.orderNumber}
                label={`${barcodeOrder.customerName} - ₺${barcodeOrder.totalAmount.toLocaleString('tr-TR')}`}
                type={barcodeType}
                onClose={() => setBarcodeOrder(null)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-xl">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Siparişi Sil</h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              Bu siparişi silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  deleteOrder(deleteConfirm);
                  setDeleteConfirm(null);
                  setSelectedOrder(null);
                }}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl transition-colors"
              >
                Sil
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-colors"
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
